import React from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import { Trans } from "react-i18next";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import StepButton from "@mui/material/StepButton";
import SFTypography from "../typography";
import SFLink from "../link";
import StepContent from "@mui/material/StepContent";
import StepConnector, { stepConnectorClasses } from "@mui/material/StepConnector";
import getStyled from "../../hooks/getStyled";

const { styled } = getStyled();

const SFLinkStyled = styled(SFLink)(({ theme, isClickable }) => ({
  cursor: isClickable ? "pointer" : "default",
  textDecoration: "none",
  "&:hover": {
    textDecoration: isClickable ? "underline" : "none",
  },
}));

// Change the color of the connector line of every completed step to green depending on the stepConnectorClasses
const StyledStepConnector = styled(StepConnector)(({ theme }) => ({
  [`&.${stepConnectorClasses.active}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      borderColor: theme.palette.green400.main,
    },
  },
  [`&.${stepConnectorClasses.completed}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      borderColor: theme.palette.green400.main,
    },
  },
  [`& .${stepConnectorClasses.line}`]: {
    borderColor: theme.palette.grey200.main,
  },
}));

// Change the border color of the StepContent component when it is active to green except for the last step
const StyledStepContent = styled(StepContent, {
  shouldForwardProps: (prop) => prop !== "active",
})(({ theme, active }) => ({
  "&:not(.MuiStepContent-last)": {
    borderLeft: "1px solid ",
    borderColor: theme.palette.grey200.main,
  },
  ...(active && {
    "&:not(.MuiStepContent-last)": {
      borderColor: theme.palette.green400.main,
    },
  }),
}));

//TODO : NEED TO INCLUDE THIS VARIANT IN THE STORY BOOK
const VerticalStepperComponent = ({
  stepsArray,
  activeStep,
  clickStepHandler,
  connector,
  stepIconComponent,
  showOnlyButtons,
  disableClickNavigation,
}) => {
  const navigate = useNavigate();
  const getLabelTypography = (label, index) => {
    const isClickable = activeStep === index;
    const handleLinkClick = () => {
      if (label.link.path) {
        navigate(label.link.path);
      } else if (label.link.onClick) {
        label.link.onClick();
      }
    };

    return (
      <SFTypography
        variant={label.variant}
        color={isClickable ? "grey500" : "grey400"}
      >
        <Trans
          i18nKey={label.translationKey}
          components={
            label.link && {
              [label.link.component]: (
                <SFLinkStyled
                  onClick={handleLinkClick}
                  isClickable={isClickable}
                />
              ),
            }
          }
        />
      </SFTypography>
    );
  };

  return (
    <Stepper
      orientation="vertical"
      sx={{ mx: 2 }}
      activeStep={activeStep}
      connector={connector ?? <StyledStepConnector />}
    >
      {stepsArray.map((step, index) => {
        return (
          <Step key={step.label.translationKey} expanded={true}>
            <StepButton
              color="inherit"
              onClick={clickStepHandler.bind(this, index)}
              disableRipple={disableClickNavigation}
              sx={{
                cursor: disableClickNavigation ? "default" : "pointer",
              }}
            >
              <StepLabel StepIconComponent={stepIconComponent}>
                {showOnlyButtons && getLabelTypography(step.label, index)}
              </StepLabel>
            </StepButton>
            <StyledStepContent active={activeStep > index}>
              {getLabelTypography(step.subLabel, index)}
            </StyledStepContent>
          </Step>
        );
      })}
    </Stepper>
  );
};

VerticalStepperComponent.propTypes = {
  stepsArray: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.shape({
        // The label text of the step.
        translationKey: PropTypes.string.isRequired,
        // Variant for the step label typography.
        variant: PropTypes.string.isRequired,
        // An optional configuration object that will be used to render a link in the translation.
        // For example, if the translation is "lorem ipsum <MyLink>dolor</MyLink> sit amet",
        // then the link object should be { component: "MyLink", path: "/my-link" } or { component: "MyLink", onClick: () => {} }.
        link: PropTypes.shape({
          // The tag name of the link wrapper in the translation (e.g. "MyLink")
          component: PropTypes.string.isRequired,
          // The path to navigate to when the link is clicked.
          path: PropTypes.string,
          // Callback function to be executed when the link is clicked and no path is provided.
          onClick: PropTypes.func,
        }),
      }),
      subLabel: PropTypes.shape({
        translationKey: PropTypes.string.isRequired,
        variant: PropTypes.string.isRequired,
        link: PropTypes.shape({
          component: PropTypes.string.isRequired,
          path: PropTypes.string,
          onClick: PropTypes.func,
        }),
      }),
    })
  ).isRequired,
  activeStep: PropTypes.number.isRequired,
  clickStepHandler: PropTypes.func.isRequired,
  connector: PropTypes.element,
  stepIconComponent: PropTypes.elementType,
  showOnlyButtons: PropTypes.bool,
};

export default VerticalStepperComponent;
